import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Role } from "../schemas/roleSchema";
import { roleService, RoleFilters } from "../api/services/roleService";

export const useRoles = (
  filters: RoleFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: roles,
    isLoading: isRolesLoading,
    refetch,
  } = useQuery({
    queryKey: ["roles", filters],
    queryFn: () => roleService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const useGetRoleById = (id?: string) =>
    useQuery<Role, Error>({
      queryKey: ["role", id],
      queryFn: () => roleService.getById(id!),
      enabled: !!id,
    });

  const createRoleMutation = useMutation({
    mutationFn: roleService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["roleOptions"] });
      toast.success("Role created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create role");
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: roleService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["roleOptions"] });
      toast.success("Role updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update role");
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: roleService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["roleOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Role deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete role");
    },
  });

  return {
    roles,
    isLoading:
      isRolesLoading ||
      createRoleMutation.isPending ||
      updateRoleMutation.isPending ||
      deleteRoleMutation.isPending,
    useGetRoleById,
    refetchRoles: refetch,
    createRole: createRoleMutation.mutate,
    updateRole: updateRoleMutation.mutate,
    deleteRoles: deleteRoleMutation.mutate,
  };
};
